import { useState } from "react";
import {
  ArrowLeft,
  FileText,
  Check,
  X,
  AlertTriangle,
  Package,
  RefreshCw,
  Eye,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showSuccessToast } from "@/components/NotificationToast";
import { mockClients, mockDocuments } from "../mockData";
import type { TaxRequestJob } from "../types";

const statusConfig: Record<string, { label: string; className: string }> = {
  processing: { label: "Processing", className: "bg-blue-100 text-blue-700 border-blue-200" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 border-green-200" },
  completed_with_issues: { label: "Issues Found", className: "bg-amber-100 text-amber-700 border-amber-200" },
};

interface TaxRequestReviewPageProps {
  job: TaxRequestJob;
  onBack: () => void;
  onJobUpdate: (job: TaxRequestJob) => void;
}

export function TaxRequestReviewPage({ job, onBack, onJobUpdate }: TaxRequestReviewPageProps) {
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [reprocessing, setReprocessing] = useState(false);

  const client = mockClients.find((c) => c.id === job.clientId);
  const status = statusConfig[job.status];

  const foundItems = job.items.filter((i) => i.status === "found");
  const missingItems = job.items.filter((i) => i.status === "not_found");
  const ambiguousItems = job.items.filter((i) => i.status === "ambiguous");

  const handleResolveAmbiguous = (itemId: string, docId: string) => {
    const updatedItems = job.items.map((item) =>
      item.id === itemId
        ? { ...item, status: "found" as const, matchedDocumentId: docId, candidateDocumentIds: [] }
        : item
    );
    const newFound = updatedItems.filter((i) => i.status === "found").length;
    const newMissing = updatedItems.filter((i) => i.status === "not_found").length;
    const newAmbiguous = updatedItems.filter((i) => i.status === "ambiguous").length;
    onJobUpdate({
      ...job,
      items: updatedItems,
      totalFound: newFound,
      totalMissing: newMissing,
      totalAmbiguous: newAmbiguous,
      status: newMissing === 0 && newAmbiguous === 0 ? "completed" : "completed_with_issues",
    });
    showSuccessToast("Document resolved");
  };

  const handleGenerateBundle = () => {
    showSuccessToast("ZIP bundle generated and ready for download");
  };

  const handleReprocess = () => {
    setReprocessing(true);
    onJobUpdate({ ...job, status: "processing" });
    setTimeout(() => {
      onJobUpdate({ ...job, status: job.totalMissing > 0 ? "completed_with_issues" : "completed" });
      setReprocessing(false);
      showSuccessToast("Re-processing complete");
    }, 2500);
  };

  const getDocName = (docId: string) => {
    const doc = mockDocuments.find((d) => d.id === docId);
    return doc?.storedFilename || doc?.originalFilename || docId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{job.requestFilename}</h2>
            <Badge variant="outline" className={`text-xs ${status.className}`}>
              {job.status === "processing" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {status.label}
            </Badge>
          </div>
          <p className="text-sm text-neutral-500">
            {client?.name} &middot; {job.requestDate} &middot;{" "}
            <span className="font-medium">{job.totalFound}</span> of{" "}
            <span className="font-medium">{job.totalRequested}</span> documents found
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Original request */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-medium text-sm mb-3">Original NRA Request</h3>
            <div className="bg-neutral-100 rounded-lg border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center py-20">
              <FileText className="w-12 h-12 text-neutral-400 mb-2" />
              <span className="text-sm text-neutral-500">{job.requestFilename}</span>
              <span className="text-xs text-neutral-400 mt-1">Preview not available in demo</span>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-4">
          {/* Found */}
          {foundItems.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Found Documents ({foundItems.length})
                </h3>
                <div className="space-y-2">
                  {foundItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm"
                    >
                      <Check className="w-4 h-4 text-green-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{item.requestedDescription}</p>
                        {item.matchedDocumentId && (
                          <p className="text-xs text-neutral-500 truncate">
                            &rarr; {getDocName(item.matchedDocumentId)}
                          </p>
                        )}
                      </div>
                      {item.matchedDocumentId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 shrink-0"
                          onClick={() => setPreviewDocId(item.matchedDocumentId)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Missing */}
          {missingItems.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <X className="w-4 h-4 text-red-600" />
                  Missing Documents ({missingItems.length})
                </h3>
                <div className="space-y-2">
                  {missingItems.map((item) => (
                    <div
                      key={item.id}
                      className="p-2 bg-red-50 rounded text-sm"
                    >
                      <p>{item.requestedDescription}</p>
                      {item.matchNotes && (
                        <p className="text-xs text-neutral-500 mt-1">{item.matchNotes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ambiguous */}
          {ambiguousItems.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Ambiguous Matches ({ambiguousItems.length})
                </h3>
                <div className="space-y-3">
                  {ambiguousItems.map((item) => (
                    <div key={item.id} className="p-3 bg-amber-50 rounded">
                      <p className="text-sm mb-1">{item.requestedDescription}</p>
                      {item.matchNotes && (
                        <p className="text-xs text-neutral-500 mb-2">{item.matchNotes}</p>
                      )}
                      <div className="flex items-center gap-2">
                        <Select
                          onValueChange={(value) => handleResolveAmbiguous(item.id, value)}
                        >
                          <SelectTrigger className="h-8 text-xs flex-1">
                            <SelectValue placeholder="Select matching document" />
                          </SelectTrigger>
                          <SelectContent>
                            {item.candidateDocumentIds.map((docId) => (
                              <SelectItem key={docId} value={docId} className="text-xs">
                                {getDocName(docId)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3 pt-2 border-t">
        <Button onClick={handleGenerateBundle} disabled={job.status === "processing"}>
          <Package className="w-4 h-4 mr-2" />
          Generate ZIP Bundle
        </Button>
        <Button
          variant="outline"
          onClick={handleReprocess}
          disabled={reprocessing || job.status === "processing"}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${reprocessing ? "animate-spin" : ""}`} />
          Re-process
        </Button>
      </div>

      {/* Preview dialog */}
      <Dialog open={!!previewDocId} onOpenChange={(open) => !open && setPreviewDocId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Document Preview</DialogTitle>
          </DialogHeader>
          <div className="bg-neutral-100 rounded-lg border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-neutral-400 mb-2" />
            <span className="text-sm text-neutral-500">
              {previewDocId && getDocName(previewDocId)}
            </span>
            <span className="text-xs text-neutral-400 mt-1">Preview not available in demo</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
